import React, { FunctionComponent, useEffect } from 'react';
import { Prompt } from '../../../shared/Prompt';
import { Paragraph } from '../../../shared/Paragraph';
import PromptImg from '../../../../assets/Prompt/registration-abandon-prompt.png';
import './styles';
import { trackEvent } from '../../../../tracking/tracking';
import {
  RegistrationAbandonShowEvent,
  RegistrationAbandonConfirmEvent,
  RegistrationAbandonContinueEvent,
} from '../../../../tracking/types';
import { RegistrationStepIds } from '../../types';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';

export type AbandonPromptProps = {
  /**
   * Whether to show the Prompt. Defaults to false
   */
  show: boolean;
  /**
   * Called when prompt should be hidden.
   */
  onDismiss: () => void;
  /**
   * Text to display as the prompt title
   */
  title: string;
  /**
   * Called if the user confirms they want to leave registration
   */
  onConfirmAbandon: () => void;
  /**
   * The id of the current step we are displaying to the user
   */
  currentStep: RegistrationStepIds;
};

export const AbandonPrompt: FunctionComponent<AbandonPromptProps> = ({
  show,
  onDismiss,
  title,
  onConfirmAbandon,
  currentStep,
}) => {
  const { t } = useTranslations();

  const confirmAbandonPrompt = (): void => {
    trackEvent<RegistrationAbandonConfirmEvent>({ event: 'regabandon_confirm', step: currentStep });
    onConfirmAbandon();
  };

  const dismissAbandonPrompt = (): void => {
    trackEvent<RegistrationAbandonContinueEvent>({ event: 'regabandon_continue', step: currentStep });
    onDismiss();
  };

  useEffect(() => {
    if (show) {
      trackEvent<RegistrationAbandonShowEvent>({ event: 'regabandon_show', step: currentStep });
    }
  }, [show]);

  return (
    <Prompt
      show={show}
      title={title}
      className="registration-abandon-prompt"
      onClose={dismissAbandonPrompt}
      image={{
        src: PromptImg,
        alt: 'prompt-img',
      }}
      PrimaryButton={{
        onClick: confirmAbandonPrompt,
        text: 'YES I WANT TO LEAVE',
        buttonVariant: 'secondary-alt',
      }}
      SecondaryButton={{
        onClick: dismissAbandonPrompt,
        text: 'CONTINUE REGISTERING',
        buttonVariant: 'primary',
      }}
      mode={'dark'}
    >
      <Paragraph noMargin size="sm" className="registration-abandon-prompt__message">
        {t(`registration.modal.abandon-prompt.message-${currentStep}`)}
      </Paragraph>
    </Prompt>
  );
};
